import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { auth } from "../api/client";
import { clearUserCaches, hasPaid, revalidateUser, useAnonymMode, useCurrentUser } from "../lib/auth";
import { refreshAll } from "../lib/swr";
import HouseholdSwitcher from "./HouseholdSwitcher";
import Logo from "./Logo";
import PullToRefresh from "./PullToRefresh";

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Optional hover/longpress hint when the label alone is ambiguous. */
  hint?: string;
  /** True = only show when the user has Logbuch Plus. */
  paid?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Übersicht", icon: "○" },
  { to: "/budget", label: "Budget", icon: "▤" },
  { to: "/goals", label: "Ziel", icon: "⌂" },
  { to: "/sparziele", label: "Kleine Sparziele", icon: "◎" },
  { to: "/historie", label: "Historie", icon: "↶" },
  { to: "/depot", label: "Depot", icon: "▲", hint: "Aktien & Fonds", paid: true },
  { to: "/reconcile", label: "Abgleich", icon: "≡", hint: "Kontostände vs. Budgetposten gegenrechnen" },
  { to: "/transactions", label: "Umsätze", icon: "↕" },
  { to: "/zuordnung", label: "Zuordnung", icon: "⤳", paid: true },
  { to: "/einstellungen", label: "Einstellungen", icon: "⚙" },
];

interface AppShellProps {
  title?: string;
  actions?: React.ReactNode;
  /** Per-page view tabs rendered in the topbar (e.g. /budget Überblick /
   *  Verwalten). When omitted, no tab strip renders. */
  views?: { id: string; label: string }[];
  view?: string;
  onViewChange?: (id: string) => void;
  children: React.ReactNode;
}

export default function AppShell({ title, actions, views, view, onViewChange, children }: AppShellProps) {
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // setAnonym lives on /einstellungen now — we just consume the flag here.
  const [anonym] = useAnonymMode();
  // Paid items stay visible even for non-paid users — but render a small
  // "Plus" pill so the user knows they exist and what unlocks them. The
  // Link href flips to the membership section for non-paid taps.
  const me = useCurrentUser();
  const paid = hasPaid(me);
  const visibleNav = NAV;
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  async function logout() {
    await auth.logout();
    // Cached identity + data must not leak into the next session on this
    // device (another user could log in).
    clearUserCaches();
    nav("/login", { replace: true });
  }

  const navItem = (active: boolean, mobile = false) =>
    "flex items-center gap-3 rounded-md px-3 text-sm transition " +
    (mobile ? "py-3 " : "py-2 ") +
    (active
      ? "bg-brand-500 text-ink-950 shadow-sm font-medium"
      : "text-ink-300 hover:bg-ink-800 hover:text-ink-100");

  // Pull-down gesture refreshes the current page from the API server only —
  // no FinTS roundtrip. Bank-sync (which is seconds-long) lives on the
  // explicit "Banken" button (BankSyncButton component, placed in each
  // page's AppShell `actions` slot where relevant — currently /Umsätze).
  //
  // Soft refresh: re-run every mounted data fetcher in place (no white
  // flash, no JS reboot). Pages that don't use the swr cache yet report 0
  // mounted keys — those keep the old full-reload rescue behavior.
  async function pullRefresh() {
    try {
      const [mountedKeys] = await Promise.all([refreshAll(), revalidateUser()]);
      if (mountedKeys === 0) window.location.reload();
    } catch {
      window.location.reload();
    }
  }

  return (
    <PullToRefresh onRefresh={pullRefresh}>
    <div className={"flex min-h-screen bg-ink-950 " + (anonym ? "anonym" : "")}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-60 md:flex-col md:border-r md:border-ink-800 md:bg-ink-900">
        <Link
          to="/"
          className="flex h-16 items-center gap-2.5 px-5 border-b border-ink-800 hover:bg-ink-800/40 transition"
          aria-label="Zur Übersicht"
        >
          <span className="text-brand-400">
            <Logo size={28} />
          </span>
          <span className="font-semibold tracking-tight text-ink-100">Logbuch</span>
        </Link>
        <div className="px-3 py-2 border-b border-ink-800/60">
          <HouseholdSwitcher />
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {visibleNav.map((n) => {
            const active = loc.pathname === n.to;
            const gated = !!n.paid && !paid;
            const href = gated ? "/einstellungen#mitgliedschaft" : n.to;
            return (
              <Link key={n.to} to={href} className={navItem(active)} title={n.hint}>
                <span className="w-4 text-center font-mono text-[13px] opacity-70">{n.icon}</span>
                <span>{n.label}</span>
                {gated && <PlusPill />}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-ink-800 space-y-1">
          {/* Rundtour + Anonym sind absichtlich nur unter /einstellungen
              erreichbar — hier sammelt sich sonst zu viel UI-Lärm. Die
              Rundtour poppt beim ersten Besuch automatisch auf (TOUR_KEY). */}
          <button onClick={logout} className="btn-ghost w-full justify-start">
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        className={
          "md:hidden fixed inset-y-0 left-0 z-40 w-64 transform bg-ink-900 border-r border-ink-800 shadow-xl transition-transform " +
          (mobileOpen ? "translate-x-0" : "-translate-x-full")
        }
      >
        <Link
          to="/"
          className="flex h-16 items-center gap-2.5 px-5 border-b border-ink-800 hover:bg-ink-800/40 transition"
          aria-label="Zur Übersicht"
        >
          <span className="text-brand-400">
            <Logo size={28} />
          </span>
          <span className="font-semibold tracking-tight text-ink-100">Logbuch</span>
        </Link>
        <nav className="p-3 space-y-1">
          {visibleNav.map((n) => {
            const active = loc.pathname === n.to;
            const gated = !!n.paid && !paid;
            const href = gated ? "/einstellungen#mitgliedschaft" : n.to;
            return (
              <Link key={n.to} to={href} className={navItem(active, true)} title={n.hint}>
                <span className="w-4 text-center font-mono text-[13px] opacity-70">{n.icon}</span>
                <span>{n.label}</span>
                {gated && <PlusPill />}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-ink-800 space-y-1.5">
          <button onClick={logout} className="btn-ghost w-full justify-start py-3">
            Logout
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Mobile topbar — minimal: hamburger + small logo + page title.
            Android runs non-edge-to-edge (see MainActivity) so the WebView
            sits below the OS status bar — no safe-area padding needed. */}
        <header
          className="md:hidden sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-ink-800 bg-ink-950/95 backdrop-blur px-2"
        >
          <button
            type="button"
            onClick={() => setMobileOpen((s) => !s)}
            className="btn-ghost flex h-11 w-11 items-center justify-center -ml-1"
            aria-label="Menü"
          >
            <span className="text-2xl leading-none">☰</span>
          </button>
          <Link to="/" className="flex items-center text-brand-400" aria-label="Logbuch">
            <Logo size={24} />
          </Link>
          {title && (
            <span
              className="font-medium text-ink-100 text-base truncate min-w-0 flex-1"
              title={title}
            >
              {title}
            </span>
          )}
          <div className="ml-auto pr-1">
            <HouseholdSwitcher compact />
          </div>
        </header>

        {/* Page header — desktop only. Mobile relies on the topbar title. */}
        {(title || actions) && (
          <div className="hidden md:block border-b border-ink-800 bg-ink-900 md:sticky md:top-0 md:z-10">
            <div className="max-w-6xl w-full mx-auto px-4 md:px-8 h-16 flex items-center gap-3">
              {title && <h1 className="page-title">{title}</h1>}
              <div className="ml-auto flex items-center gap-2 flex-wrap">{actions}</div>
            </div>
          </div>
        )}

        {/* Mobile sub-bar — split into two compact rows: actions on top
            (month-stepper, bank-sync, + Neu …), filter tabs below. Was a
            single side-by-side row before but on /Umsätze that crammed
            three buttons + a tab strip into one horizontal-scroll lane,
            which read as "fat" and made the actions hard to spot.
            Desktop still gets one combined row since its actions live in
            the page header anyway. */}
        {((views && views.length > 1) || actions) && (
          <div className="border-b border-ink-800 bg-ink-900/95 backdrop-blur sticky top-14 md:top-16 z-[9]">
            <div className="relative">
              {/* Row 1 (mobile only) — actions. Desktop hides this. */}
              {actions && (
                <div className="md:hidden border-b border-ink-800/60">
                  <div className="max-w-6xl w-full mx-auto px-3 flex items-center gap-2 py-2 overflow-x-auto whitespace-nowrap [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {actions}
                  </div>
                </div>
              )}
              {/* Row 2 — filter tabs (views). On desktop this is the whole
                  sub-bar (one row, no actions slot since desktop puts
                  those in the page header above). */}
              <div className="max-w-6xl w-full mx-auto px-3 md:px-8 flex items-center gap-3 overflow-x-auto whitespace-nowrap [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {views && views.length > 1 && (
                  <div className="flex items-center gap-1 shrink-0">
                    {views.map((v) => {
                      const active = v.id === view;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => onViewChange?.(v.id)}
                          className={
                            "relative px-3 py-3 text-sm transition whitespace-nowrap " +
                            (active
                              ? "text-brand-200 font-medium"
                              : "text-ink-400 hover:text-ink-100")
                          }
                        >
                          {v.label}
                          {active && (
                            <span
                              aria-hidden="true"
                              className="absolute inset-x-2 bottom-0 h-0.5 rounded-t bg-brand-400"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-ink-900 to-transparent"
              />
            </div>
          </div>
        )}

        <main className="flex-1 px-3 py-3 md:px-8 md:py-8 max-w-6xl w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
    </PullToRefresh>
  );
}

// Small "Plus" pill rendered next to nav items that require a subscription.
// Tapping the row goes to /einstellungen#mitgliedschaft so the marker
// double-doubles as the upgrade entry point.
function PlusPill() {
  return (
    <span className="ml-auto rounded-full bg-brand-500/15 text-brand-300 text-[10px] font-semibold px-1.5 py-0.5 leading-none">
      Plus
    </span>
  );
}
